/* eslint-disable max-len */
/* eslint-disable no-await-in-loop */
/* eslint-disable max-statements */

import { Button, Center, Group, Loader, LoadingOverlay, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { capitalize, get, isEqual, uniqWith } from 'lodash';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { Batch, DeclarationStatus, FunctionalError, IDeclarationsForUpdateData } from '@e-origin/shared';

import { Select, TextInput } from '@components';
import { BULK_UPDATE_BATCH_SIZE } from '@constants';
import { useBulkUpdateBatch, useUpdateBatchStatistics } from '@hooks/batch';
import { useDeclarationsByBatchIdAndStatus } from '@hooks/declaration';
import { useCustomErrors } from '@hooks/useCustomErrors';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { IDropDownOption } from '@interfaces';

interface BulkUpdateBatchProps {
  afterCreate(): void;
  onHide(): void;
  updatedBatch?: Batch;
}

export interface NewBulUpdateData {
  errorReason: string;
  errorPointer: string;
  errorRemark: string;
  initialValue: string;
  modifyValue: string;
}

const ERROR_MESSAGE_PLACEHOLDER = 'Error message';
const ERROR_POINTER_PLACEHOLDER = 'Error pointer';
const ERROR_REMARKS_PLACEHOLDER = 'Error remarks';
const INITIAL_VALUE_PLACEHOLDER = 'Initial value';
const MODIFY_VALUE_PLACEHOLDER = 'Modify value';
const ERROR_POINTER = {
  GOODS_SHIPMENT_ITEM: 'IE001.GoodsShipment.GoodsShipmentItem',
  GOVERNMENT_AGENCY_GOODS_ITEM: 'GoodsShipment/*:GovernmentAgencyGoodsItem',
};

const bulkUpdateSchema = z.object({
  errorReason: z.string().min(1, { message: 'Required' }),
  errorPointer: z.string().min(1, { message: 'Required' }),
  errorRemark: z.string().min(1, { message: 'Required' }),
  initialValue: z.string(),
  modifyValue: z.string().min(1, { message: 'Required' }),
});

type BulkUpdateValues = z.infer<typeof bulkUpdateSchema>;

export const BulkUpdateBatch: React.FC<BulkUpdateBatchProps> = (props) => {
  const { afterCreate, onHide, updatedBatch } = props;

  const [error, setError] = useState('');
  const [errorPointers, setErrorPointers] = useState<IDropDownOption[]>([]);
  const [errorRemarks, setErrorRemarks] = useState<IDropDownOption[]>([]);
  const [initialValues, setInitialValues] = useState<IDropDownOption[]>([]);

  const { data: customsErrors = [] } = useCustomErrors();
  const { data: declarations, isLoading } = useDeclarationsByBatchIdAndStatus({ batchId: updatedBatch?._id, status: DeclarationStatus.REJECTED });

  const bulkUpdateBatchMutation = useBulkUpdateBatch();
  const updateBatchStatisticsMutation = useUpdateBatchStatistics();

  const form = useFormWithNameInputProp<BulkUpdateValues>({
    initialValues: {
      errorReason: '',
      errorPointer: '',
      errorRemark: '',
      initialValue: '',
      modifyValue: '',
    },
    validate: zodResolver(bulkUpdateSchema),
    validateInputOnBlur: true,
  });

  const { errorReason, errorPointer, errorRemark, initialValue, modifyValue } = form.values;

  const errorReasons = uniqWith(
    declarations.reduce<IDropDownOption[]>((accumulator, rejectedDeclaration) => {
      rejectedDeclaration?.customsState?.functionalError.forEach((err) => {
        const foundedMessage = customsErrors.find((customsError) => customsError.errorReason === err.errorReason);

        accumulator.push({
          label: foundedMessage?.errorMessage ?? err.errorReason,
          value: err.errorReason,
        });
      });

      return accumulator;
    }, []),
    isEqual,
  );

  const onSubmit = async (values: BulkUpdateValues) => {
    const declarationsForUpdateData: IDeclarationsForUpdateData[] = [];

    if (!!values.errorReason && !!values.errorPointer && !!values.errorRemark && !!values.modifyValue) {
      declarations.forEach((declaration) => {
        declaration?.customsState?.functionalError.forEach((err) => {
          const foundedMessage = customsErrors.find((customsError) => customsError.errorReason === values.errorReason);

          const existingDeclarationForUpdateData = declarationsForUpdateData.find((d) => d.declarationId === declaration._id);

          if (
            err.remarks === values.errorRemark &&
            err.errorPointer === values.errorPointer &&
            (err.errorPointer.includes(ERROR_POINTER.GOODS_SHIPMENT_ITEM) || err.errorPointer.includes(ERROR_POINTER.GOVERNMENT_AGENCY_GOODS_ITEM)) &&
            foundedMessage
          ) {
            // it extracts idx index from IE001.GoodsShipment.GoodsShipmentItem[idx]
            // where idx starts from 1 (we must shift left by 1 position)
            const idx = Number(errorPointer.split('[')[1][0]) - 1;
            if (initialValue === get(declaration, foundedMessage.declarationAttributePointer.replace('?', idx.toString()))) {
              const dataChanges = [
                {
                  fieldName: `goodsShipmentItems[${idx}].commodityCode.hsCode`,
                  newFieldValue: values.modifyValue,
                  oldFieldValue: get(declaration, `goodsShipmentItems[${idx}].commodityCode.hsCode`),
                },
                {
                  fieldName: `goodsShipmentItems[${idx}].commodityCode.hsCodeCustomsSystem`,
                  newFieldValue: values.modifyValue,
                  oldFieldValue: get(declaration, `goodsShipmentItems[${idx}].commodityCode.hsCodeCustomsSystem`),
                },
              ];
              if (!existingDeclarationForUpdateData) {
                declarationsForUpdateData.push({
                  declarationId: declaration?._id ?? '',
                  dataChanges,
                });
              } else {
                existingDeclarationForUpdateData.dataChanges.push(...dataChanges);
              }
            }
            return;
          }

          if (
            err.remarks === values.errorRemark &&
            err.errorPointer === values.errorPointer &&
            foundedMessage &&
            initialValue === get(declaration, foundedMessage.declarationAttributePointer)
          ) {
            const dataChange = {
              fieldName: foundedMessage.declarationAttributePointer,
              newFieldValue: values.modifyValue,
              oldFieldValue: get(declaration, foundedMessage.declarationAttributePointer),
            };
            if (!existingDeclarationForUpdateData) {
              declarationsForUpdateData.push({
                declarationId: declaration?._id ?? '',
                dataChanges: [dataChange],
              });
            } else {
              existingDeclarationForUpdateData.dataChanges.push(dataChange);
            }
          }
        });
      });
      for (let index = 0; index < declarationsForUpdateData.length; index += BULK_UPDATE_BATCH_SIZE) {
        const nextIndex = index + BULK_UPDATE_BATCH_SIZE;
        const arrayToSend = declarationsForUpdateData.slice(index, nextIndex);

        await bulkUpdateBatchMutation.mutateAsync({ batchId: updatedBatch?._id ?? '', declarationsForUpdate: arrayToSend });
      }

      const declarationsIdsForUpdate = declarationsForUpdateData.map((declarationForUpdate) => {
        return declarationForUpdate.declarationId;
      });

      await updateBatchStatisticsMutation.mutateAsync({ batchId: updatedBatch?._id ?? '', declarationsIdsForUpdate });

      afterCreate();
      onHide();
    }
  };

  const functionalErrors = uniqWith(
    declarations.reduce<FunctionalError[]>((accumulator, declaration) => {
      declaration?.customsState?.functionalError.forEach((err) => {
        if (err.errorReason === errorReason) {
          accumulator.push(err);
        }
      });

      return accumulator;
    }, []),
    isEqual,
  );

  const handleSetErrorRemarks = () => {
    const errRemarks = uniqWith(
      functionalErrors.map((functionalError) => {
        return {
          label: functionalError.remarks,
          value: functionalError.remarks,
        };
      }),
      isEqual,
    );
    setErrorRemarks(errRemarks);
  };

  const handleSetErrorPointers = () => {
    const pointers = new Set(
      functionalErrors.filter(({ remarks, errorPointer }) => errorRemark === remarks && errorPointer).map(({ errorPointer }) => errorPointer),
    );

    setErrorPointers([...pointers].map((p) => ({ label: p, value: p })));
  };

  const handleSetInitialValues = () => {
    const initialValuesData: IDropDownOption[] = [];

    let declarationsCounter = 0;

    declarations.forEach((declaration) => {
      declaration?.customsState?.functionalError.forEach((err) => {
        customsErrors.forEach((customsError) => {
          if (customsError.errorReason !== err.errorReason) {
            return;
          }
          const isAddInitialValue = customsError.errorReason === errorReason && err.remarks === errorRemark && err.errorPointer === errorPointer;

          if (isAddInitialValue) {
            if (errorPointer.includes(ERROR_POINTER.GOODS_SHIPMENT_ITEM) || errorPointer.includes(ERROR_POINTER.GOVERNMENT_AGENCY_GOODS_ITEM)) {
              // it extracts idx index from IE001.GoodsShipment.GoodsShipmentItem[idx]
              // where idx starts from 1 (we must shift left by 1 position)
              const idx = Number(errorPointer.split('[')[1][0]) - 1;
              initialValuesData.push({
                label: get(declaration, customsError.declarationAttributePointer.replace('?', idx.toString())),
                value: get(declaration, customsError.declarationAttributePointer.replace('?', idx.toString())),
              });
            } else {
              initialValuesData.push({
                label: get(declaration, customsError.declarationAttributePointer),
                value: get(declaration, customsError.declarationAttributePointer),
              });
            }
          }
        });

        if (err.remarks === errorRemark) {
          declarationsCounter += 1;
        }
      });
    });

    const isInitialValuesDataEmpty = initialValuesData.length === 0;

    if (!isInitialValuesDataEmpty) {
      setInitialValues(uniqWith(initialValuesData, isEqual));
    }

    if (initialValues.length === 1) {
      form.setFieldValue('initialValue', initialValues[0].value);
    }

    if (isInitialValuesDataEmpty) {
      const wordEnding = declarationsCounter === 1 ? ' is' : 's are';

      const bulkUpdateError = `${declarationsCounter} declaration${wordEnding} having this error but the correction cannot be handled here. Please use the batch file to modify your declarations`;

      setError(bulkUpdateError);
    }
  };

  const isErrorPointerEmpty = errorPointers.length === 0;
  const isErrorRemarksEmpty = errorRemarks.length === 0;
  const isInitialValuesEmpty = initialValues.length === 0;
  const isInitialValuesMultiple = initialValues.length > 1;

  useEffect(() => {
    if (errorReason) {
      handleSetErrorRemarks();
    }

    if (errorRemark) {
      handleSetErrorPointers();
    }

    if (errorPointer && errorReason && errorRemark) {
      handleSetInitialValues();
    }
  }, [errorReason, errorPointer, errorRemark, isErrorPointerEmpty, isErrorRemarksEmpty, isInitialValuesEmpty]);

  const isProcessButtonDisabled = !errorReason || !errorPointer || !errorRemark || !modifyValue;

  const errorPointerData = errorPointer.split('.');

  const getInitialValuePlaceholder = () => {
    let placeholder = errorPointerData[errorPointerData.length - 1];
    if (errorPointer.includes(ERROR_POINTER.GOODS_SHIPMENT_ITEM)) {
      placeholder = 'HS Code';
    }
    return capitalize(placeholder);
  };
  const initialValuePlaceholder = getInitialValuePlaceholder();

  if (!declarations) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      {isLoading ? (
        <Center h={200}>
          <Loader />
        </Center>
      ) : (
        <form onSubmit={form.onSubmit(onSubmit)} data-pw="bulk-update-form">
          <Stack gap="sm" mb="xl">
            <Select
              data-pw="error-reason-select"
              {...form.getInputProps('errorReason')}
              label={ERROR_MESSAGE_PLACEHOLDER}
              data={errorReasons}
              searchable
              size="lg"
              onChange={(value) => {
                form.setValues({
                  errorReason: value ?? '',
                  errorPointer: '',
                  errorRemark: '',
                  initialValue: '',
                  modifyValue: '',
                });
                setError('');
                setErrorPointers([]);
                setErrorRemarks([]);
                setInitialValues([]);
              }}
              maxDropdownHeight={200}
            />

            {!isErrorRemarksEmpty && (
              <Select
                data-pw="error-remarks-select"
                {...form.getInputProps('errorRemark')}
                label={ERROR_REMARKS_PLACEHOLDER}
                data={errorRemarks}
                searchable
                size="lg"
                onChange={(value) => {
                  form.setValues({
                    errorReason,
                    errorRemark: value ?? '',
                    errorPointer: '',
                    initialValue: '',
                    modifyValue: '',
                  });
                  setError('');
                  setErrorPointers([]);
                  setInitialValues([]);
                }}
                maxDropdownHeight={200}
              />
            )}

            {!isErrorPointerEmpty && (
              <Select
                data-pw="error-pointer-select"
                {...form.getInputProps('errorPointer')}
                label={ERROR_POINTER_PLACEHOLDER}
                data={errorPointers}
                searchable
                size="lg"
                onChange={(value) => {
                  form.setValues({
                    errorReason,
                    errorRemark,
                    errorPointer: value ?? '',
                    initialValue: '',
                    modifyValue: '',
                  });
                  setError('');
                  setInitialValues([]);
                }}
                maxDropdownHeight={200}
              />
            )}

            {!isInitialValuesEmpty && !isInitialValuesMultiple && (
              <>
                <TextInput {...form.getInputProps('initialValue')} label={initialValuePlaceholder} value={initialValues[0].value} disabled size="lg" />

                <TextInput {...form.getInputProps('modifyValue')} label={MODIFY_VALUE_PLACEHOLDER} size="lg" data-pw="modify-value-input" />
              </>
            )}

            {!isInitialValuesEmpty && isInitialValuesMultiple && (
              <>
                <Select
                  {...form.getInputProps('initialValue')}
                  label={INITIAL_VALUE_PLACEHOLDER}
                  data={initialValues}
                  searchable
                  size="lg"
                  onChange={(value) => {
                    form.setValues({
                      errorReason,
                      errorRemark,
                      errorPointer,
                      initialValue: value,
                      modifyValue,
                    });
                  }}
                  maxDropdownHeight={200}
                />

                <TextInput {...form.getInputProps('modifyValue')} label={MODIFY_VALUE_PLACEHOLDER} size="lg" />
              </>
            )}

            {!!error && (
              <Text c="#555454" fw="normal" pb={50}>
                {error}
              </Text>
            )}
          </Stack>

          <Group justify="center">
            <Button type="button" variant="transparent" onClick={onHide} size="lg">
              Cancel
            </Button>

            <Button data-pw="continue-btn" type="submit" disabled={isProcessButtonDisabled} variant="filled" size="lg">
              Continue
            </Button>
          </Group>
        </form>
      )}
    </>
  );
};
